import { useNavigate, useParams } from 'react-router-dom';
import {
  branchSelector,
  brandsSelector,
  createBranch,
  getAllBranches,
  getBranchMenu,
  getBranchesByBrandId,
  kitchensSelector,
  mealSelector,
  removeCoverageRadius,
  updateBranch,
  updateBranchName,
  updateCoverageRadius,
  updateBranchBusinessHours,
  variantSelector,
} from '../store/slices';
import { useAppDispatch, useAppSelector } from './redux.hooks';
import { SyntheticEvent, useEffect, useState } from 'react';
import {
  CreateBranch,
  IBrand,
  IKitchen,
  IMeal,
  IVariant,
  UpdateBranch,
  CoverageRadiusPayload,
  IBusinessHours,
} from '../types';
import { IOnChange } from '../views/components';
import { toast } from 'react-toastify';

export const useBranch = (page?: boolean) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { loading, branches, branchesByBrand, branchMenu } =
    useAppSelector(branchSelector);
  const { kitchens } = useAppSelector(kitchensSelector);
  const { brands } = useAppSelector(brandsSelector);
  const { variants } = useAppSelector(variantSelector);
  const { meals } = useAppSelector(mealSelector);

  let { id } = useParams();

  const [payload, setPayload] = useState<CreateBranch>({
    kitchenId: 0,
    brandId: 0,
    name: '',
  });
  const [updatePayload, setUpdatePayload] = useState<UpdateBranch>({
    mealOverrides: [],
    variantOverrides: [],
  });
  const [coveragePayload, setCoveragePayload] = useState<CoverageRadiusPayload>(
    {
      brandId: 0,
      kitchenId: 0,
      coverageRadius: 0,
    }
  );
  const [modal, setModal] = useState({ visible: false, data: {} });
  const [branchMeals] = useState<IMeal[]>([]);
  const [branchVariants] = useState<IVariant[]>([]);
  const [branchName, setBranchName] = useState('');
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [showBusinessHoursModal, setShowBusinessHoursModal] = useState(false);
  const [businessHours, setBusinessHours] = useState<IBusinessHours>({
    openState: 'OPEN',
    hours: [],
  });

  const createBranchPage = () => {
    navigate(`/dashboard/branch/create`);
  };

  const _editBranch = (slug: string, e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (slug) {
      return navigate(`/dashboard/branch/edit/${slug}`);
    }
    return navigate(`/dashboard/branch/create`);
  };

  const branchPage = (slug: string) => {
    navigate(`/dashboard/branches/${slug}`);
  };

  const branchesPage = () => {
    navigate(`/dashboard/vendors`);
  };

  const onChangeKitchen: IOnChange<IKitchen> = (kitchen) => {
    if (kitchen) {
      setPayload({ ...payload, kitchenId: (kitchen as IKitchen).id });
      setCoveragePayload({
        ...coveragePayload,
        kitchenId: (kitchen as IKitchen).id,
      });
    }
  };

  const onChangeBrand: IOnChange<IBrand> = (brand) => {
    if (brand) {
      setPayload({ ...payload, brandId: (brand as IBrand).id });
      setCoveragePayload({ ...coveragePayload, brandId: (brand as IBrand).id });
    }
  };

  const _createBranch = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();
    dispatch(createBranch({ payload, cb: branchesPage }));
  };

  const fetchBranchesByBrandId = (brandId: number | string) => {
    dispatch(getBranchesByBrandId(brandId));
  };

  const openCoverageModal = (branch: any) => {
    setSelectedBranch(branch);
    setCoveragePayload({
      brandId: branch.brandId,
      kitchenId: branch.kitchenId,
      coverageRadius: branch.coverageRadius || 0,
    });
    setShowCoverageModal(true);
  };

  const closeCoverageModal = () => {
    setShowCoverageModal(false);
    setSelectedBranch(null);
  };

  const _updateCoverageRadius = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();

    dispatch(
      updateCoverageRadius({
        payload: coveragePayload,
        cb: () => {
          closeCoverageModal();
          selectedBranch?.brandId &&
            dispatch(getBranchesByBrandId(selectedBranch.brandId));
        },
      })
    );
  };

  const _removeCoverageRadius = (branch: any) => {
    dispatch(
      removeCoverageRadius({
        payload: {
          brandId: branch.brandId,
          kitchenId: branch.kitchenId,
        },
        cb: () => {
          branch.brandId && dispatch(getBranchesByBrandId(branch.brandId));
        },
      })
    );
  };

  const handleOverrideChange = (
    e: SyntheticEvent<HTMLSelectElement | HTMLInputElement>,
    override?: 'mealOverrides' | 'variantOverrides'
  ) => {
    let temp: Record<string, any> = { ...updatePayload };

    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    if (override) {
      if (override === 'mealOverrides') {
        temp.mealOverrides[0][name === 'price' ? 'basePrice' : name] =
          name === 'price' ? parseInt(value) : (value as any);
      }

      if (override === 'variantOverrides') {
        temp.variantOverrides[0][name] =
          name === 'price' ? parseInt(value) : (value as any);
      }
    }

    setUpdatePayload(temp as UpdateBranch);
  };

  const _updateBranch = (
    e?: SyntheticEvent<HTMLFormElement>,
    payload?: UpdateBranch
  ) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (id)
      dispatch(
        updateBranch({
          payload: payload ?? updatePayload,
          cb: () => {
            branchesPage();
            dispatch(getAllBranches());
            id && dispatch(getBranchMenu(id));
          },
          id,
        })
      );
  };

  const _updateBranchName = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (id)
      dispatch(
        updateBranchName({
          payload: { name: branchName },
          cb: () => {
            branchesPage();
            dispatch(getAllBranches());
          },
          id,
        })
      );
  };

  const handleCoverageRadiusChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = parseFloat(e.currentTarget.value);
    setCoveragePayload({
      ...coveragePayload,
      coverageRadius: value,
    });
  };

  const openModal = (
    event: SyntheticEvent<HTMLButtonElement>,
    id?: number,
    name?: string
  ) => {
    event.stopPropagation();
    if (id && name) {
      let temp;

      if (name === 'mealOverrides') {
        const meal = branchMeals.find((meal) => meal.id === id);

        if (meal) {
          temp = {
            ...updatePayload,
            mealOverrides: [
              {
                mealId: meal.id,
                basePrice: meal.basePrice,
                status: meal.status,
              },
            ],
          };
          setModal({ visible: true, data: temp });
        }
      } else if (name === 'variantOverrides') {
        const variant = branchVariants.find((variant) => variant!.id === id);

        if (variant) {
          temp = {
            ...updatePayload,
            variantOverrides: [
              {
                variantId: variant.id,
                price: variant.price,
                status: variant.status,
              },
            ],
          };
          setModal({ visible: true, data: temp });
        }
      }
    }
  };

  const closeModal = () => {
    setUpdatePayload({
      ...updatePayload,
      mealOverrides: [],
      variantOverrides: [],
    });

    setModal({ visible: false, data: {} });
  };

  const toggleAvailability = (
    module: 'mealOverrides' | 'variantOverrides',
    active: boolean,
    item: any
  ) => {
    if (id) {
      if (module === 'mealOverrides') {
        dispatch(
          updateBranch({
            payload: {
              mealOverrides: [
                {
                  mealId: parseInt(`${item.id}`),
                  status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
                  basePrice: item.basePrice,
                },
              ],
              variantOverrides: [],
            },
            cb: () => id && dispatch(getBranchMenu(id)),
            id,
          })
        );
      }

      if (module === 'variantOverrides') {
        dispatch(
          updateBranch({
            payload: {
              variantOverrides: [
                {
                  variantId: parseInt(`${item.id}`),
                  status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
                  price: item.price,
                },
              ],
              mealOverrides: [],
            },
            cb: () => id && dispatch(getBranchMenu(id)),
            id,
          })
        );
      }
    }
  };

  const openBusinessHoursModal = (branch: any) => {
    setSelectedBranch(branch);
    setBusinessHours(
      branch.business || {
        openState: 'OPEN',
        hours: [],
      }
    );
    setShowBusinessHoursModal(true);
  };

  const closeBusinessHoursModal = () => {
    setShowBusinessHoursModal(false);
    setSelectedBranch(null);
  };

  const _updateBranchBusinessHours = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (!selectedBranch) return;

    dispatch(
      updateBranchBusinessHours({
        payload: businessHours,
        branchId: selectedBranch.id,
        cb: () => {
          closeBusinessHoursModal();
          toast.success('Branch business hours updated successfully');
          selectedBranch?.brandId &&
            dispatch(getBranchesByBrandId(selectedBranch.brandId));
        },
      })
    );
  };

  useEffect(() => {
    if (id && page) {
      dispatch(getBranchMenu(id));
    }
    // eslint-disable-next-line
  }, [id]);

  return {
    loading,
    openModal,
    closeModal,
    branches,
    branchesByBrand,
    fetchBranchesByBrandId,
    payload,
    setPayload,
    updatePayload,
    branchMenu,
    kitchens,
    brands,
    meals,
    variants,
    branchMeals,
    branchVariants,
    createBranchPage,
    branchPage,
    branchesPage,
    _createBranch,
    handleOverrideChange,
    _updateBranch,
    _editBranch,
    modal,
    branchName,
    setBranchName,
    _updateBranchName,
    toggleAvailability,
    onChangeKitchen,
    onChangeBrand,
    coveragePayload,
    setCoveragePayload,
    handleCoverageRadiusChange,
    _updateCoverageRadius,
    _removeCoverageRadius,
    openCoverageModal,
    closeCoverageModal,
    showCoverageModal,
    selectedBranch,
    openBusinessHoursModal,
    closeBusinessHoursModal,
    _updateBranchBusinessHours,
    showBusinessHoursModal,
    businessHours,
    setBusinessHours,
  };
};
